import { useAuth } from '../hooks/useAuth'
import { useIncome } from '../hooks/useData'
import { colors, fonts } from '../components/UI'
import { useMemo } from 'react'
import { getPayCycle, cycleRangeLabel } from '../lib/payday'

// Colored square icon
function SqIcon({ emoji, color }) {
  return (
    <div style={{ width: '44px', height: '44px', borderRadius: '12px', background: `${color}20`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '22px', flexShrink: 0 }}>
      {emoji}
    </div>
  )
}

function MoreItem({ emoji, color, title, sub, onNavigate, id, last }) {
  return (
    <div onClick={() => onNavigate(id)} style={{ display: 'flex', alignItems: 'center', gap: '14px', padding: '14px 16px', borderBottom: last ? 'none' : '1px solid rgba(255,255,255,0.06)', cursor: 'pointer' }}>
      <SqIcon emoji={emoji} color={color} />
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: '15px', fontWeight: 600, color: colors.text }}>{title}</div>
        <div style={{ fontSize: '13px', fontWeight: 500, color: colors.muted, marginTop: '2px' }}>{sub}</div>
      </div>
      <div style={{ fontSize: '16px', color: `rgba(${colors.textRgb},0.3)` }}>›</div>
    </div>
  )
}

export default function MorePage({ onNavigate }) {
  const { user, profile, signOut } = useAuth()
  const payDay = profile?.pay_day
  const shiftWeekends = profile?.pay_day_shift_weekends !== false
  const currentCycle = useMemo(() => getPayCycle(payDay, new Date(), shiftWeekends), [payDay, shiftWeekends])
  const basicMode = profile?.basic_mode === true

  const isDual = profile?.income_type === 'dual'
  const displayName = profile?.full_name || user?.email?.split('@')[0] || 'User'
  const initials = displayName.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase()
  const firstName = displayName.split(' ')[0]
  const { incomeForCycle } = useIncome()
  const cycleIncome = currentCycle ? incomeForCycle(currentCycle) : 0
  const daysLeft = currentCycle ? Math.max(0, Math.ceil((new Date(currentCycle.endIso) - new Date()) / 86400000)) : null
  const memberSince = user?.created_at ? new Date(user.created_at).toLocaleDateString('en-GB', { month: 'short', year: 'numeric' }) : null
  const daysSince = user?.created_at ? Math.ceil((new Date() - new Date(user.created_at)) / 86400000) : null

  const MONEY = basicMode ? [] : [
    { id: 'income', emoji: '🏦', color: '#3db87a', title: 'Income', sub: 'Manage your income sources' },
  ]
  const TOOLS = basicMode ? [] : [
    { id: 'insights',  emoji: '📊', color: '#4a8fe8', title: 'Insights',             sub: 'Spending history and financial trends' },
    { id: 'blueprint', emoji: '🎯', color: '#3db87a', title: 'Financial Blueprint',  sub: 'Review your 50/30/20 progress' },
    { id: 'learn',     emoji: '🎓', color: '#e8b84b', title: 'Learn',               sub: 'Guides, tips and financial education' },
    { id: 'import',    emoji: '📥', color: '#8b5cf6', title: 'Import data',          sub: 'Bulk import accounts or bills from CSV' },
  ]
  const ACCOUNT = [
    { id: 'settings',      emoji: '👤', color: '#4a8fe8', title: 'Profile & household',     sub: 'Manage your details and household members' },
    { id: 'notifications', emoji: '🔔', color: '#8b5cf6', title: 'Notifications',           sub: 'Manage reminders and alerts' },
  ]

  return (
    <div style={{ paddingBottom: '40px' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', padding: '18px 20px 4px', position: 'relative' }}>
        <div style={{ flex: 1, textAlign: 'center' }}>
          <div style={{ fontFamily: fonts.heading, fontWeight: 700, fontSize: '20px', color: colors.text }}>More</div>
        </div>
      </div>

      <div style={{ padding: '14px 16px 0' }}>

        {/* Profile card */}
        <div style={{ background: colors.card, border: `1px solid ${colors.border}`, borderRadius: '16px', overflow: 'hidden', marginBottom: '20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '14px', padding: '16px', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
            <div style={{ width: '52px', height: '52px', borderRadius: '50%', background: 'rgba(201,151,43,0.15)', border: '2px solid rgba(201,151,43,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: fonts.heading, fontSize: '20px', fontWeight: 700, color: colors.gold, flexShrink: 0 }}>{initials}</div>
            <div style={{ flex: 1 }}>
              <div style={{ fontFamily: fonts.heading, fontSize: '18px', fontWeight: 700, color: colors.text }}>{displayName}</div>
              <div style={{ fontSize: '13px', fontWeight: 500, color: colors.muted, marginTop: '2px' }}>{isDual ? 'Dual household' : 'Single household'}</div>
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1px 1fr', gap: 0 }}>
            <div style={{ padding: '12px 10px', textAlign: 'center' }}>
              <div style={{ fontSize: '12px', fontWeight: 700, letterSpacing: '0.07em', color: colors.muted, marginBottom: '4px' }}>PAY PERIOD</div>
              <div style={{ fontSize: '12px', fontWeight: 700, color: colors.text, lineHeight: 1.4 }}>
                {currentCycle
                  ? new Date(currentCycle.startIso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) + ' - ' + new Date(currentCycle.endIso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })
                  : '-'}
              </div>
              <div style={{ fontSize: '12px', fontWeight: 600, color: colors.gold, marginTop: '2px' }}>{daysLeft} days left</div>
            </div>
            <div style={{ background: 'rgba(255,255,255,0.06)' }} />
            <div style={{ padding: '12px 10px', textAlign: 'center' }}>
              <div style={{ fontSize: '12px', fontWeight: 700, letterSpacing: '0.07em', color: colors.muted, marginBottom: '4px' }}>MEMBER SINCE</div>
              <div style={{ fontSize: '13px', fontWeight: 700, color: colors.text }}>{memberSince || '-'}</div>
              <div style={{ fontSize: '12px', fontWeight: 500, color: colors.muted, marginTop: '2px' }}>Tracking for {daysSince} day{daysSince !== 1 ? 's' : ''}</div>
            </div>
          </div>
        </div>

        {/* Money */}
        {MONEY.length > 0 && <>
          <div style={{ fontSize: '12px', fontWeight: 700, letterSpacing: '0.07em', color: colors.muted, marginBottom: '8px' }}>MONEY</div>
          <div style={{ background: colors.card, border: `1px solid ${colors.border}`, borderRadius: '16px', overflow: 'hidden', marginBottom: '20px' }}>
            {MONEY.map((item, i) => <MoreItem key={item.id} {...item} onNavigate={onNavigate} last={i === MONEY.length - 1} />)}
          </div>
        </>}

        {/* Tools & Features */}
        {TOOLS.length > 0 && <>
          <div style={{ fontSize: '12px', fontWeight: 700, letterSpacing: '0.07em', color: colors.muted, marginBottom: '8px' }}>TOOLS & FEATURES</div>
          <div style={{ background: colors.card, border: `1px solid ${colors.border}`, borderRadius: '16px', overflow: 'hidden', marginBottom: '20px' }}>
            {TOOLS.map((item, i) => <MoreItem key={item.id} {...item} onNavigate={onNavigate} last={i === TOOLS.length - 1} />)}
          </div>
        </>}

        {/* Account */}
        <div style={{ fontSize: '12px', fontWeight: 700, letterSpacing: '0.07em', color: colors.muted, marginBottom: '8px' }}>ACCOUNT</div>
        <div style={{ background: colors.card, border: `1px solid ${colors.border}`, borderRadius: '16px', overflow: 'hidden', marginBottom: '20px' }}>
          {ACCOUNT.map((item, i) => <MoreItem key={item.id} {...item} onNavigate={onNavigate} last={i === ACCOUNT.length - 1} />)}
        </div>

        {/* Support */}
        <div style={{ fontSize: '12px', fontWeight: 700, letterSpacing: '0.07em', color: colors.muted, marginBottom: '8px' }}>SUPPORT</div>
        <div style={{ background: colors.card, border: `1px solid ${colors.border}`, borderRadius: '16px', overflow: 'hidden', marginBottom: '20px' }}>
          <MoreItem emoji="❓" color="#e8b84b" id="appguide" title="App guide" sub="Getting started and key concepts" onNavigate={onNavigate} />
          <div onClick={() => window.location.href = 'mailto:hello@theinvestingcouple.co.uk'} style={{ display: 'flex', alignItems: 'center', gap: '14px', padding: '14px 16px', cursor: 'pointer' }}>
            <div style={{ width: '44px', height: '44px', borderRadius: '12px', background: 'rgba(74,143,232,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '22px', flexShrink: 0 }}>✉️</div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: '15px', fontWeight: 600, color: colors.text }}>Contact us</div>
              <div style={{ fontSize: '13px', fontWeight: 500, color: colors.muted, marginTop: '2px' }}>hello@theinvestingcouple.co.uk</div>
            </div>
            <div style={{ fontSize: '16px', color: `rgba(${colors.textRgb},0.3)` }}>›</div>
          </div>
        </div>

        {/* Sign out */}
        <div style={{ background: colors.card, border: `1px solid ${colors.border}`, borderRadius: '14px', overflow: 'hidden', marginBottom: '16px' }}>
          <div onClick={signOut} style={{ display: 'flex', alignItems: 'center', gap: '14px', padding: '14px 16px', cursor: 'pointer' }}>
            <SqIcon emoji="⎋" color={colors.red} />
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: '15px', fontWeight: 600, color: colors.red }}>Sign out</div>
              <div style={{ fontSize: '13px', fontWeight: 500, color: colors.muted, marginTop: '2px' }}>{user?.email}</div>
            </div>
          </div>
        </div>

        <div style={{ background: 'rgba(201,151,43,0.06)', border: '1px solid rgba(201,151,43,0.15)', borderRadius: '14px', padding: '14px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <div style={{ fontSize: '14px', fontWeight: 600, color: colors.text }}>The Starting Line</div>
            <div style={{ fontSize: '13px', fontWeight: 500, color: colors.muted, marginTop: '2px' }}>Free beginner investing course</div>
          </div>
          <a href="https://theinvestingcouple.co.uk/starting-line/" target="_blank" rel="noopener noreferrer" style={{ fontSize: '13px', fontWeight: 700, color: colors.gold, textDecoration: 'none', whiteSpace: 'nowrap' }}>Open ↗</a>
        </div>

        <div style={{ textAlign: 'center', padding: '16px 0 4px' }}>
          <a href="https://logo.dev" target="_blank" rel="noopener noreferrer" style={{ fontSize: '11px', fontWeight: 500, color: `rgba(${colors.textRgb},0.42)`, textDecoration: 'none' }}>Logos provided by Logo.dev</a>
        </div>
      </div>
    </div>
  )
}
